import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@adiox/ui';
import { useStream } from '../stream/stream';
import { useActiveLogin } from '../trading/useTrading';
import type { ChallengeDetail } from './model';

export const CHALLENGE_REFRESH_MS = 3_000;

/**
 * The prop-firm challenge behind the active account: null when the account is an ordinary one. The server computes every figure
 * (the terminal never evaluates a rule); this refreshes every few seconds and at once when the stream reports a deal or a
 * challenge event.
 */
export function useChallenge() {
  const api = useApi();
  const login = useActiveLogin();
  const queryClient = useQueryClient();
  const lastEvent = useStream((s) => s.recentEvents.at(-1)?.seq ?? 0);
  const seen = useRef(lastEvent);
  useEffect(() => {
    if (lastEvent !== seen.current) {
      seen.current = lastEvent;
      void queryClient.invalidateQueries({ queryKey: ['challenge'] });
    }
  }, [lastEvent, queryClient]);

  return useQuery<ChallengeDetail | null>({
    queryKey: ['challenge', login],
    enabled: login !== undefined,
    refetchInterval: CHALLENGE_REFRESH_MS,
    retry: false,
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const { data, response } = await api.GET('/api/v1/accounts/{login}/challenge', {
        params: { path: { login: login! } },
      });
      if (response.status === 204 || response.status === 404) return null;
      if (!data) throw new Error('The challenge could not be loaded.');
      return data;
    },
  });
}
